import { useEffect, useState } from 'react'
import { Card } from '@/components/Card'
import { fetchApi } from '@/lib/api'
import { AlertTriangle, Play, RefreshCw, Square } from 'lucide-react'
import { formatDateTime } from '@/lib/date'

type LiveRunner = {
  runner_id: string
  hostname?: string
  phase?: string
  current_item_id?: string | null
  last_seen_at?: string
  version?: string
}

type Instance = {
  instance_id: string
  name: string | null
  environment: string
  state: string
  runner_id: string | null
  last_action: string | null
  last_error: string | null
  updated_at: string
  slots: string[]
  is_live: boolean
  live: LiveRunner | null
}

type Slot = {
  slot_name: string
  needs_reauth: boolean
  note: string | null
  assigned_to: string[]
  shared: boolean
}

type Warning = {
  kind: string
  message: string
}

type Fleet = {
  instances: Instance[]
  slots: Slot[]
  runners: LiveRunner[]
  warnings: Warning[]
  live_error: string | null
}

const STATE_STYLE: Record<string, string> = {
  running: 'bg-green-500/15 text-green-400',
  draining: 'bg-yellow-500/15 text-yellow-400',
  stopped: 'bg-gray-500/15 text-gray-400',
}

export function Runners() {
  const [fleet, setFleet] = useState<Fleet | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  const load = async () => {
    setLoading(true)
    try {
      setFleet(await fetchApi<Fleet>('/runners/fleet'))
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // ドレインは実行中アイテムの完走を待つので数十分かかる。進行は台帳を読み直して見る
    const timer = setInterval(load, 30_000)
    return () => clearInterval(timer)
  }, [])

  const post = async (path: string, body?: unknown) => {
    setBusy(path)
    try {
      await fetchApi(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined })
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(null)
      await load()
    }
  }

  const drainStop = (instanceId: string) => {
    if (
      !confirm(
        `${instanceId} をドレイン停止します。\n\n` +
          '新規のアイテム取得を止め、実行中アイテムが完走してから EC2 を停止します。' +
          '完走待ちのため最大75分かかることがあります（完走しなければ停止しません）。',
      )
    ) {
      return
    }
    void post(`/runners/instances/${instanceId}/drain-stop`)
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">Agent Loop Runners</h2>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-1 px-3 py-1 text-sm rounded bg-[var(--color-bg-hover)] hover:bg-[var(--color-border)] transition-colors"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="px-4 py-2 rounded bg-red-500/15 text-red-400 text-sm">{error}</div>
      )}

      {fleet?.live_error && (
        <div className="px-4 py-2 rounded bg-yellow-500/15 text-yellow-400 text-sm">
          ランナー登録（生死）を取得できませんでした: {fleet.live_error}
          <span className="block text-[var(--color-text-muted)]">
            表示中の状態は台帳の記録のみで、実機の生死は未確認です。
          </span>
        </div>
      )}

      {fleet && fleet.warnings.length > 0 && (
        <Card title="警告">
          <ul className="space-y-1 text-sm">
            {fleet.warnings.map((w, i) => (
              <li key={i} className="flex items-start gap-2 text-yellow-400">
                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                <span>{w.message}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card title="ランナー機">
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[var(--color-text-muted)] text-left">
            <tr>
              <th className="py-2">Instance</th>
              <th>台帳の状態</th>
              <th>稼働</th>
              <th>枠</th>
              <th>最後の操作</th>
              <th className="text-right">操作</th>
            </tr>
          </thead>
          <tbody>
            {fleet?.instances.map((i) => (
              <tr key={i.instance_id} className="border-t border-[var(--color-border)]">
                <td className="py-2">
                  <div className="font-mono">{i.instance_id}</div>
                  <div className="text-[var(--color-text-muted)]">
                    {i.name ?? '-'} / {i.environment} / runner={i.runner_id ?? '未登録'}
                  </div>
                </td>
                <td>
                  <span
                    className={`px-2 py-0.5 rounded text-xs ${
                      STATE_STYLE[i.state] ?? 'bg-gray-500/15 text-gray-400'
                    }`}
                  >
                    {i.state}
                  </span>
                </td>
                <td>
                  {i.is_live ? (
                    <span className="text-green-400">
                      live{i.live?.phase ? ` (${i.live.phase})` : ''}
                    </span>
                  ) : (
                    <span className="text-[var(--color-text-muted)]">-</span>
                  )}
                </td>
                <td className="font-mono">{i.slots.length ? i.slots.join(', ') : '-'}</td>
                <td>
                  <div>{i.last_action ?? '-'}</div>
                  {i.last_error && <div className="text-red-400">{i.last_error}</div>}
                  <div className="text-[var(--color-text-muted)]">
                    {formatDateTime(i.updated_at)}
                  </div>
                </td>
                <td className="text-right whitespace-nowrap">
                  <button
                    onClick={() => void post(`/runners/instances/${i.instance_id}/start`)}
                    disabled={busy !== null || i.state === 'draining'}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded bg-[var(--color-bg-hover)] hover:bg-[var(--color-border)] disabled:opacity-40"
                  >
                    <Play size={14} /> 起動
                  </button>
                  <button
                    onClick={() => drainStop(i.instance_id)}
                    disabled={busy !== null || i.state !== 'running'}
                    className="ml-2 inline-flex items-center gap-1 px-2 py-1 rounded bg-[var(--color-bg-hover)] hover:bg-[var(--color-border)] disabled:opacity-40"
                  >
                    <Square size={14} /> ドレイン停止
                  </button>
                </td>
              </tr>
            ))}
            {fleet?.instances.length === 0 && (
              <tr>
                <td colSpan={6} className="py-4 text-[var(--color-text-muted)]">
                  台帳にランナー機がありません。
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      </Card>

      <Card title="AI アカウント枠">
        <p className="text-xs text-[var(--color-text-muted)] mb-2">
          1枠を複数台へ払い出すことは許可されています（共有は現行運用）。意図しない共有を
          見つけるために「共有中」を表示します。再認証が必要な枠は払い出されません。
        </p>
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[var(--color-text-muted)] text-left">
            <tr>
              <th className="py-2">枠</th>
              <th>払い出し先</th>
              <th>状態</th>
              <th className="text-right">操作</th>
            </tr>
          </thead>
          <tbody>
            {fleet?.slots.map((s) => (
              <tr key={s.slot_name} className="border-t border-[var(--color-border)]">
                <td className="py-2 font-mono">{s.slot_name}</td>
                <td className="font-mono">
                  {s.assigned_to.length ? s.assigned_to.join(', ') : '-'}
                </td>
                <td>
                  {s.needs_reauth && (
                    <span className="px-2 py-0.5 rounded text-xs bg-red-500/15 text-red-400">
                      要再認証
                    </span>
                  )}
                  {s.shared && (
                    <span className="ml-1 px-2 py-0.5 rounded text-xs bg-yellow-500/15 text-yellow-400">
                      共有中（{s.assigned_to.length}台）
                    </span>
                  )}
                  {!s.needs_reauth && !s.shared && (
                    <span className="text-[var(--color-text-muted)]">-</span>
                  )}
                  {s.note && (
                    <div className="text-[var(--color-text-muted)]">{s.note}</div>
                  )}
                </td>
                <td className="text-right">
                  <button
                    onClick={() =>
                      void post('/runners/slots/reauth', {
                        slot_name: s.slot_name,
                        needs_reauth: !s.needs_reauth,
                      })
                    }
                    disabled={busy !== null}
                    className="px-2 py-1 rounded bg-[var(--color-bg-hover)] hover:bg-[var(--color-border)] disabled:opacity-40"
                  >
                    {s.needs_reauth ? '再認証済みにする' : '要再認証にする'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </Card>
    </div>
  )
}
